import { localStorageAvailable } from '@utils/localStorage';
import cloneDeep from 'lodash/cloneDeep';
import getTextColor from 'aquilla/core/shared/utils/getTextColor';
import { nFormatter } from '@utils/textHelper';
import pushRoute from '@utils/pushRoute';
import { getLinkPath } from '@models/Course';
import { getLink } from '@models/Item';
import { sessionStates } from '@constants/index';
import {
  getDateMonthString,
  getDateShort,
  getTime,
  isSameDay,
  timeAgoSinceDate
} from '@utils/dates';
import { getClassCard } from '@cont/NewPreSubscribed/PopularClasses';
import { isFuture } from 'date-fns';
import { TOPICS } from '../constants';

export const getEducatorsListFromLocalStorage = () => {
  if (localStorageAvailable()) {
    return (
      JSON.parse(localStorage.getItem('educatorsWithEducatorLibraryBanner')) ||
      []
    );
  }
};

export const addEducatorInLocalStorage = (username) => {
  if (localStorageAvailable()) {
    const educatorsList = getEducatorsListFromLocalStorage();
    const updatedList = [...educatorsList, username];
    localStorage.setItem(
      'educatorsWithEducatorLibraryBanner',
      JSON.stringify(updatedList)
    );
  }
};

export const isEducatorLibraryEnabled = (results = []) =>
  results.some((feedData) => feedData.type === TOPICS);

export const getShowEducatorLibraryIntroBanner = (results = [], username) =>
  results.length > 0 &&
  !getEducatorsListFromLocalStorage()?.includes(username) &&
  isEducatorLibraryEnabled(results);

export const getFiltersApplied = (query) => {
  return {
    goal: { id: query.goal || '' },
    topic: { id: query.topic || '' },
    filter_by: { id: 'dpp' }
  };
};

export const getFilterQueryParams = ({ filter, option, filtersApplied }) => {
  const applyFilters = cloneDeep(filtersApplied ?? {});
  applyFilters[filter].id = option?.id;
  let queryParams = '';
  if (applyFilters.filter_by?.id)
    queryParams += `type=${applyFilters.filter_by?.id}`;
  if (applyFilters.goal?.id) queryParams += `&goal=${applyFilters.goal?.id}`;
  if (applyFilters.topic?.id) queryParams += `&topic=${applyFilters.topic?.id}`;
  return queryParams;
};

const classStatesMap = {
  [sessionStates.FUTURE]: 'Upcoming',
  [sessionStates.PAST]: 'Completed',
  [sessionStates.DORMANT]: 'Dormont'
};

function getClassVariant(classInfo) {
  const { isLive, isUpcoming } = classInfo;
  if (isLive) return 'Live';
  if (isUpcoming) return 'Upcoming';
  return 'Completed';
}

function getUpcomingDateString(startTime) {
  const isToday = isSameDay(new Date(startTime), new Date());
  const isTomorrow = isSameDay(
    new Date(startTime),
    new Date(new Date().getTime() + 24 * 60 * 60 * 1000)
  );

  if (isToday) {
    return `Today, ${getTime(startTime)}`;
  }
  if (isTomorrow) {
    return `Tomorrow, ${getTime(startTime)}`;
  }
  return `Starts on ${getDateShort(startTime)}`;
}

const getFormattedClassDate = (
  startsAt: string,
  endsAt: string,
  isLive: boolean
) => {
  if (!startsAt && !endsAt) {
    return '';
  }

  // Live class
  if (isLive) {
    return `Started at ${getTime(startsAt)}`;
  }

  // Upcoming class
  const isUpcomingClass = isFuture(new Date(startsAt));
  if (isUpcomingClass) {
    return getUpcomingDateString(startsAt);
  }

  // Past class
  const isPastClass = !!endsAt;
  if (isPastClass) {
    return timeAgoSinceDate(startsAt);
  }

  return getDateMonthString(new Date(startsAt));
};

export const getClassDetailsLine = (classInfo) => {
  const { startsAt, endsAt, isLive, viewCount } = classInfo;

  const formattedViewString = isLive ? 'viewer' : 'view';
  const formattedViewCount = `${nFormatter(viewCount)} ${
    viewCount > 1 ? `${formattedViewString}s` : formattedViewString
  }`;

  const formattedTimeString = getFormattedClassDate(startsAt, endsAt, isLive);

  const divider = viewCount && formattedTimeString ? '  •  ' : '';

  // Eg. 2.5K viewers • Started at 4:30 PM
  return `${
    viewCount > 0 ? formattedViewCount : ''
  }${divider}${formattedTimeString}`;
};

export function formatClassDetail({
  classInfo,
  hideDetailsLine = true,
  onClick
}) {
  const { author, name, topicGroups, languageDisplay, thumbnail, notified } =
    classInfo;
  const variant = getClassVariant(classInfo);

  const handleOnClick = () => {
    if (onClick) onClick(classInfo);
    const route = {
      href: getLinkPath(classInfo),
      as: getLink(classInfo)
    };
    pushRoute(route.href, route.as);
  };

  const classDetail = {
    educatorLink: `/@${author.username}`,
    title: name,
    topicName: topicGroups?.[0]?.name,
    topicColor: topicGroups?.[0]?.name
      ? getTextColor(topicGroups?.[0]?.name[0] || '')
      : '#8873FF',
    langLabel: languageDisplay,
    ...(variant && { variant }),
    thumbnail: getClassCard(
      thumbnail,
      getTextColor(topicGroups?.[0]?.name[0] || '')
    ),
    onClick: handleOnClick,
    detailsLine: hideDetailsLine ? '' : getClassDetailsLine(classInfo),
    showNotifyButton: true,
    notified
  };

  return classDetail;
}
